<template>
  <section class="related-articles py-6 md:pt-10 md:pb-15">
    <h2
      class="text-center sm:text-left section-heading sm:w-full mx-auto sm:mx-0"
    >
      {{ headerText }}
    </h2>

    <div
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-8 mt-8 mb-8 md:mb-10"
    >
      <template v-if="isLoading">
        <div v-for="i in 4" :key="i">
          <UtilsSkeletonLoading class="rounded-lg h-40 w-full mb-4 card-img" />
          <UtilsSkeletonLoading class="w-full h-14 mb-3" />
          <UtilsSkeletonLoading class="w-full h-12" />
        </div>
      </template>
      <template v-else-if="contentsData.length === 0">
        <p class="text-bw-900 text-base">No Article(s) found</p>
      </template>
      <template v-else>
        <ArticleCard
          v-for="content in contentsData"
          :key="content.id"
          :with-logo="false"
          :title="content.attributes?.card_title"
          :desc="content.attributes?.layout_component?.[0]?.description"
          :image-url="content.attributes?.image?.data?.attributes?.url"
          :slug="`/ideas/${content.attributes?.slug}`"
        />
      </template>
    </div>

    <div class="mx-auto text-center">
      <NuxtLink
        :to="{
          path: '/ideas',
          query: route.path.includes('/expertises')
            ? { expertise: filterExpertise }
            : route.path.includes('/industries')
              ? { industry: filterIndustry }
              : {}
        }"
        title="Articles"
        class="btn btn-md md:btn-lg btn-primary-reverse w-max mx-auto"
      >
        View All Articles
      </NuxtLink>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  headerText: {
    type: String,
    required: true
  },
  pageSize: {
    type: Number,
    default: 8
  },
  filterIndustry: {
    type: String,
    default: null
  },
  filterExpertise: {
    type: String,
    default: null
  },
  filterExpertises: {
    type: Array,
    default: null
  },
  filterSolution: {
    type: String,
    default: null
  },
  filterProduct: {
    type: String,
    default: null
  }
})

const isLoading = ref(true)
const route = useRoute()

const contentsData = ref([])

const getContents = async () => {
  const query = {
    'pagination[pageSize]': props.pageSize,
    populate:
      'image,layout_component,layout_component.type,study_case_client.image',
    'filters[layout][$eq]': 'article',
    sort: 'publish_at:desc'
  }

  if (props.filterIndustry) {
    query['filters[$or][0][article_industries][title][$in][0]'] =
      props.filterIndustry
  }

  if (props.filterExpertises) {
    props.filterExpertises.forEach((item, index) => {
      query[`filters[$or][1][expertices][id][$in][${index}]`] = item.id
    })
  } else if (props.filterExpertise) {
    query['filters[$or][2][expertices][title][$in]'] = props.filterExpertise
  }

  if (props.filterSolution) {
    query['filters[$or][3][solutions][title][$in]'] = props.filterSolution
  }

  if (props.filterProduct) {
    query[`filters[$or][4][products][title][$in]`] = props.filterProduct
  }

  const { data, error, pending } = await useSuitmediaApiData('/api/contents', {
    query
  })
  isLoading.value = pending.value

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: `Case Studies does not exist`
    })
  }

  return data.value
}

const contents = await getContents()
contentsData.value = contents.data
</script>

<style lang="scss" scoped></style>
